import { apiPost, ApiError, handleApiError } from './apiClient';
import {
  VehicleAutoSearch,
  StartAutoSearchRequest,
  EndAutoSearchRequest,
  AutoSearchState,
  AutoSearchHistoryResponse,
} from '../models/vehicleAutoSearch';
import {
  VehicleAutoNegoSession,
  StartAutoNegoRequest,
  EndAutoNegoRequest,
  AutoNegoState,
} from '../models/vehicleAutoNegoSession';
import { Theme } from '@mui/material';
import i18n from '../i18n';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export interface AutoSessionsResponse {
  autoSearches: { [vehicleId: number]: VehicleAutoSearch | null };
  autoNegoSessions: { [vehicleId: number]: VehicleAutoNegoSession | null };
}

/**
 * Utility functions for status colors and labels
 */
export const getAutoSearchStatusColor = (theme: Theme, session: VehicleAutoSearch | null): string => {
  if (!session || !session.autoSearchState) return theme.palette.text.light;
  switch (session.autoSearchState) {
    case AutoSearchState.ON:
      return theme.palette.text.successGreen;
    case AutoSearchState.ERRORED:
      return theme.palette.text.red;
    default:
      return theme.palette.text.light;
  }
};

export const getAutoNegoStatusColor = (theme: Theme, session: VehicleAutoNegoSession | null): string => {
  if (!session || !session.autoNegoState) return theme.palette.text.light;
  switch (session.autoNegoState) {
    case AutoNegoState.ON:
      return theme.palette.text.successGreen;
    case AutoNegoState.WAITING_RESULTS:
    case AutoNegoState.RESULT_TO_REVIEW:
      return theme.palette.text.warningOrange;
    case AutoNegoState.COMPLETED:
      return theme.palette.text.light;
    case AutoNegoState.ERRORED:
      return theme.palette.text.red;
    default:
      return theme.palette.text.light;
  }
};

export const getAutoSearchStatusLabel = (t: (key: string) => string, session: VehicleAutoSearch | null, loading: boolean = false): string => {
  if (loading) return t('Loading...');
  if (!session) return t('Auto Search: OFF');
  switch (session.autoSearchState) {
    case AutoSearchState.ON:
      return t('Auto Search: ON');
    case AutoSearchState.ERRORED:
      return t('Auto Search: ERROR');
    case AutoSearchState.OFF:
    default:
      return t('Auto Search: OFF');
  }
};

export const getAutoNegoStatusLabel = (t: (key: string) => string, session: VehicleAutoNegoSession | null, loading: boolean = false): string => {
  if (loading) return t('Loading...');
  if (!session) return t('Auto Nego: OFF');
  
  switch (session.autoNegoState) {
    case AutoNegoState.ON:
      return t('Auto Nego: ON');
    case AutoNegoState.WAITING_RESULTS:
      return t('Auto Nego: WAITING');
    case AutoNegoState.RESULT_TO_REVIEW:
      return t('Auto Nego: REVIEW');
    case AutoNegoState.COMPLETED:
      return t('Auto Nego: COMPLETED');
    case AutoNegoState.ERRORED:
      return t('Auto Nego: ERROR');
    case AutoNegoState.OFF:
    default:
      return t('Auto Nego: OFF');
  }
};

export const getAutoSearchShortStatusLabel = (t: (key: string) => string, state: AutoSearchState): string => {
  switch (state) {
    case AutoSearchState.ON:
      return t('ON');
    case AutoSearchState.ERRORED:
      return t('ERROR');
    case AutoSearchState.OFF:
    default:
      return t('OFF');
  }
};

export const getAutoNegoShortStatusLabel = (t: (key: string) => string, state: AutoNegoState): string => {
  switch (state) {
    case AutoNegoState.ON:
      return t('ON');
    case AutoNegoState.WAITING_RESULTS:
      return t('WAITING');
    case AutoNegoState.RESULT_TO_REVIEW:
      return t('REVIEW');
    case AutoNegoState.COMPLETED:
      return t('COMPLETED');
    case AutoNegoState.ERRORED:
      return t('ERROR');
    case AutoNegoState.OFF:
    default:
      return t('OFF');
  }
};

// Maps known marketplace error codes (e.g. DATE_OUT_OF_RANGE) and server messages to user-friendly text.
export const parseAutoSearchError = (statusMessage: string | undefined, t: (key: string) => string): string => {
  if (!statusMessage) return t('Auto-search encountered an error. Open the session info for details.');

  if (statusMessage.includes('DATE_OUT_OF_RANGE')) {
    return t('The loading date is out of range. Please adjust the dates in vehicle settings.');
  }
  if (statusMessage.includes('No preferredRoutes configured')) {
    return t('No preferred routes configured. Please set up preferred routes in tenant settings.');
  }
  if (statusMessage.includes('No assigned route configured')) {
    return t('No assigned route configured for this vehicle. Please assign a route in vehicle settings.');
  }
  if (statusMessage.includes('No marketplaces enabled')) {
    return t('No marketplaces enabled for this account. Contact support to enable marketplace access.');
  }
  if (statusMessage.includes('No valid search booking')) {
    return t('No valid booking found for search. The vehicle needs an active booking to determine the search origin.');
  }
  if (statusMessage.includes('timeout') || statusMessage.includes('ETIMEDOUT')) {
    return t('Marketplace connection timed out. The search will retry automatically.');
  }
  if (statusMessage.includes('status 400') || statusMessage.includes('status 500')) {
    return t('Marketplace returned an error. This is usually temporary — the search will retry.');
  }

  const shortened = statusMessage.length > 120 ? statusMessage.substring(0, 120) + '…' : statusMessage;
  return shortened;
};

/**
 * Auto Search & Auto Nego API Functions
 */

/**
 * Get auto sessions for multiple vehicles in a single call
 */
export const getAutoSessions = async (vehicleIds: number[]): Promise<AutoSessionsResponse> => {
  try {
    if (vehicleIds.length === 0) {
      return {
        autoSearches: {},
        autoNegoSessions: {}
      };
    }

    const response = await apiPost(`${API_BASE_URL}/api/v1/vehicles/auto_sessions`, {
      vehicle_ids: vehicleIds
    });
    const data = await response.json();
    return data.result;
  } catch (error: unknown) {
    handleApiError(error, (i18n.t as any)('Failed to fetch auto sessions'));
    
    // Check if error is a validation failure and refresh vehicle list
    if (error instanceof ApiError && error.errorData?.error?.errorKey === 'validation.failed') {
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
    throw error;
  }
};

/**
 * Get auto search history (sessions + merged search records) for multiple vehicles
 */
export const getAutoSearchHistory = async (
  vehicleIds: number[],
  limit?: number
): Promise<AutoSearchHistoryResponse> => {
  try {
    if (vehicleIds.length === 0) {
      return {};
    }

    const body: { vehicle_ids: number[]; limit?: number } = { vehicle_ids: vehicleIds };
    if (limit !== undefined) {
      body.limit = limit;
    }

    const response = await apiPost(`${API_BASE_URL}/api/v1/vehicles/auto_search_history`, body);
    const data = await response.json();
    return data.result as AutoSearchHistoryResponse;
  } catch (error: unknown) {
    handleApiError(error, (i18n.t as any)('Failed to fetch auto search history'));
    throw error;
  }
};

/**
 * Start a new auto search session
 */
export const startAutoSearch = async (
  vehicleId: number,
  params: StartAutoSearchRequest
): Promise<void> => {
  await apiPost(`${API_BASE_URL}/api/v1/vehicle/${vehicleId}/start_search`, params);
};

/**
 * End the current auto search session
 */
export const endAutoSearch = async (
  vehicleId: number,
  params: EndAutoSearchRequest
): Promise<void> => {
  await apiPost(`${API_BASE_URL}/api/v1/vehicle/${vehicleId}/end_search`, params);
};

/**
 * Auto Negotiation API Functions
 */

/**
 * Start a new auto negotiation session
 */
export const startAutoNego = async (
  vehicleId: number,
  params: StartAutoNegoRequest
): Promise<void> => {
  await apiPost(`${API_BASE_URL}/api/v1/vehicle/${vehicleId}/start_nego`, params);
};

/**
 * End the current auto negotiation session
 */
export const endAutoNego = async (
  vehicleId: number,
  params: EndAutoNegoRequest
): Promise<void> => {
  await apiPost(`${API_BASE_URL}/api/v1/vehicle/${vehicleId}/end_nego`, params);
};
